import { getStoredLanguage, translateNow } from '../../i18n/I18nProvider';
// src/shared/maintenance/maintenanceBus.js
// Operation Agent / 서버 PubSub에서 전달되는 maintenance 이벤트를
// user-client 전체에서 동일하게 소비하기 위한 초경량 브라우저 이벤트 버스.

export const MAINTENANCE_EVENT_NAME = 'npc:maintenance:update';
export const MAINTENANCE_CLEAR_EVENT_NAME = 'npc:maintenance:clear';
export const MAINTENANCE_STORAGE_KEY = 'npc:maintenance:active';

export const MAINTENANCE_SOCKET_EVENTS = Object.freeze([
  'maintenanceNotice',
  'maintenanceCountdownUpdate',
  'maintenanceJoinBlocked',
  'maintenanceSafeStopStart',
  'maintenanceCompleted',
  'maintenanceCancelled',
  'maintenanceEvent',
  'MAINTENANCE_EVENT',
  'MAINTENANCE_NOTICE',
  'MAINTENANCE_COUNTDOWN_UPDATE',
  'MAINTENANCE_JOIN_BLOCKED',
  'MAINTENANCE_SAFE_STOP_START',
  'MAINTENANCE_COMPLETED',
  'MAINTENANCE_CANCELLED',
]);

const STALE_MAINTENANCE_GRACE_MS = 2 * 60 * 60 * 1000;

const PHASE_BY_EVENT = Object.freeze({
  maintenanceNotice: 'notice',
  maintenanceCountdownUpdate: 'notice',
  maintenanceJoinBlocked: 'join_blocked',
  maintenanceSafeStopStart: 'safe_stop',
  maintenanceCompleted: 'completed',
  maintenanceCancelled: 'cancelled',
  maintenanceEvent: 'notice',
  MAINTENANCE_EVENT: 'notice',
  MAINTENANCE_NOTICE: 'notice',
  MAINTENANCE_COUNTDOWN_UPDATE: 'notice',
  MAINTENANCE_JOIN_BLOCKED: 'join_blocked',
  MAINTENANCE_SAFE_STOP_START: 'safe_stop',
  MAINTENANCE_COMPLETED: 'completed',
  MAINTENANCE_CANCELLED: 'cancelled',
});

function safeString(v, fallback = '') {
  if (v == null) return fallback;
  return String(v).trim();
}

function toNumber(v, fallback = null) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function parseMaybeJson(v) {
  if (!v || typeof v !== 'string') return v;
  const s = v.trim();
  if (!s) return v;
  if (!(s.startsWith('{') || s.startsWith('['))) return v;
  try { return JSON.parse(s); } catch (_) { return v; }
}

function extractMaintenancePayload(payload) {
  const parsed = parseMaybeJson(payload);
  const raw = parsed && typeof parsed === 'object' ? parsed : {};
  const nested =
    raw.maintenance ||
    raw.schedule ||
    raw.data ||
    raw.payload ||
    raw.event ||
    raw.active ||
    null;
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    return { ...raw, ...nested };
  }
  return raw;
}

export function normalizeMaintenanceEvent(eventName = '', payload = {}, source = '') {
  const raw = extractMaintenancePayload(payload);
  const startAt = safeString(raw.start_at ?? raw.startAt ?? raw.start_time ?? raw.startTime ?? '');
  const updatedAt = safeString(raw.updated_at ?? raw.updatedAt ?? raw.created_at ?? raw.createdAt ?? '');
  const endAt = safeString(raw.end_at ?? raw.endAt ?? raw.expected_end_at ?? raw.expectedEndAt ?? raw.ends_at ?? raw.endsAt ?? '');
  const now = Date.now();
  const startMs = Date.parse(startAt || '');
  const explicitRemaining = toNumber(raw.remaining_ms ?? raw.remainingMs, null);
  const remainingMs = explicitRemaining != null
    ? Math.max(0, explicitRemaining)
    : (Number.isFinite(startMs) ? Math.max(0, startMs - now) : null);

  const rawEventType = safeString(raw.event_type ?? raw.eventType ?? raw.type ?? '');
  const phase = safeString(raw.phase || PHASE_BY_EVENT[rawEventType] || PHASE_BY_EVENT[eventName] || 'notice').toLowerCase();
  const scheduleId = safeString(raw.schedule_id ?? raw.scheduleId ?? raw.id ?? raw.maintenance_id ?? raw.maintenanceId ?? '');
  const blockBeforeMinutes = toNumber(raw.block_before_minutes ?? raw.blockBeforeMinutes, null);
  const noticeBeforeMinutes = toNumber(raw.notice_before_minutes ?? raw.noticeBeforeMinutes, null);

  const targetsRaw = raw.targets ?? raw.target_services ?? raw.targetServices ?? [];
  const targets = Array.isArray(targetsRaw)
    ? targetsRaw.map((x) => safeString(x)).filter(Boolean)
    : safeString(targetsRaw).split(',').map((x) => safeString(x)).filter(Boolean);

  const normalized = {
    ...raw,
    event_type: rawEventType,
    eventType: rawEventType,
    event_name: eventName,
    eventName,
    source: source || raw.source || 'socket',
    schedule_id: scheduleId,
    scheduleId,
    title: safeString(raw.title || raw.name || ''),
    message: safeString(raw.message || raw.text || raw.notice || ''),
    start_at: startAt,
    startAt,
    end_at: endAt,
    endAt,
    expected_end_at: endAt,
    expectedEndAt: endAt,
    phase,
    remaining_ms: remainingMs,
    remainingMs,
    notice_before_minutes: noticeBeforeMinutes,
    noticeBeforeMinutes,
    block_before_minutes: blockBeforeMinutes,
    blockBeforeMinutes,
    targets,
    action: safeString(raw.action || 'notice_only'),
    execute_pm2_action: raw.execute_pm2_action === true || raw.executePm2Action === true,
    updated_at: updatedAt || new Date(now).toISOString(),
    updatedAt: updatedAt || new Date(now).toISOString(),
    received_at: new Date(now).toISOString(),
    receivedAt: new Date(now).toISOString(),
  };

  return normalized;
}

export function isMaintenanceTerminal(maintenance) {
  const phase = safeString(maintenance?.phase).toLowerCase();
  const ev = safeString(maintenance?.event_name || maintenance?.eventName);
  return phase === 'completed' || phase === 'cancelled' || ev === 'maintenanceCompleted' || ev === 'maintenanceCancelled' || ev === 'MAINTENANCE_COMPLETED' || ev === 'MAINTENANCE_CANCELLED';
}

export function isMaintenanceVisible(maintenance) {
  if (!maintenance || typeof maintenance !== 'object') return false;
  if (isMaintenanceTerminal(maintenance)) return false;
  const startAt = safeString(maintenance.start_at ?? maintenance.startAt);
  const startMs = Date.parse(startAt || '');
  const now = Date.now();
  const phase = safeString(maintenance.phase).toLowerCase();
  // game server가 실제로 stop되면 completed 이벤트를 못 받을 수 있으므로,
  // start_at 이후 너무 오래 지난 local snapshot은 프론트에서 자체 만료한다.
  if (Number.isFinite(startMs) && now - startMs > STALE_MAINTENANCE_GRACE_MS) return false;
  if (phase === 'safe_stop' || phase === 'join_blocked' || phase === 'notice') return true;
  if (Number.isFinite(startMs)) {
    const remaining = startMs - now;
    return remaining >= -60 * 60 * 1000;
  }
  return !!(maintenance.message || maintenance.title);
}

export function isMaintenanceJoinBlocked(maintenance) {
  if (!isMaintenanceVisible(maintenance)) return false;
  const phase = safeString(maintenance?.phase).toLowerCase();
  if (phase === 'join_blocked' || phase === 'safe_stop') return true;
  const remainingMs = toNumber(maintenance?.remaining_ms ?? maintenance?.remainingMs, null);
  const blockBeforeMinutes = toNumber(maintenance?.block_before_minutes ?? maintenance?.blockBeforeMinutes, null);
  if (remainingMs != null && blockBeforeMinutes != null) {
    return remainingMs <= Math.max(0, blockBeforeMinutes) * 60 * 1000;
  }
  return false;
}

export function saveMaintenanceSnapshot(maintenance) {
  if (typeof window === 'undefined') return;
  try {
    if (!maintenance || isMaintenanceTerminal(maintenance)) {
      window.sessionStorage.removeItem(MAINTENANCE_STORAGE_KEY);
      return;
    }
    window.sessionStorage.setItem(MAINTENANCE_STORAGE_KEY, JSON.stringify(maintenance));
  } catch (_) {}
}

export function readMaintenanceSnapshot() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MAINTENANCE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isMaintenanceVisible(parsed)) {
      window.sessionStorage.removeItem(MAINTENANCE_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch (_) {
    try { window.sessionStorage.removeItem(MAINTENANCE_STORAGE_KEY); } catch (__) {}
    return null;
  }
}

export function emitMaintenanceUpdate(maintenance) {
  if (typeof window === 'undefined') return;
  const terminal = isMaintenanceTerminal(maintenance);
  saveMaintenanceSnapshot(terminal ? null : maintenance);
  try {
    window.dispatchEvent(new CustomEvent(terminal ? MAINTENANCE_CLEAR_EVENT_NAME : MAINTENANCE_EVENT_NAME, {
      detail: terminal ? null : maintenance,
    }));
  } catch (_) {}
}

export function dispatchMaintenanceSocketEvent(eventName = '', payload = {}, source = 'socket') {
  const normalized = normalizeMaintenanceEvent(eventName, payload, source);
  emitMaintenanceUpdate(normalized);
  return normalized;
}

export function formatRemaining(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n < 0) return '';
  const totalSec = Math.ceil(n / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days > 0) return translateNow('maintenance.remaining.daysHours', { days, hours }, `${days}d ${hours}h`);
  if (hours > 0) return translateNow('maintenance.remaining.hoursMinutes', { hours, minutes }, `${hours}h ${minutes}m`);
  if (minutes > 0) return translateNow('maintenance.remaining.minutes', { minutes }, `${minutes}m`);
  return translateNow('maintenance.remaining.seconds', { seconds }, `${seconds}s`);
}

export function buildMaintenanceText(maintenance) {
  if (!maintenance) return '';
  const lang = getStoredLanguage();
  const containsHangul = (value) => /[가-힣]/.test(String(value || ''));
  const rawMessage = safeString(maintenance.message);
  const rawTitle = safeString(maintenance.title);
  const message = lang !== 'ko' && containsHangul(rawMessage) ? '' : rawMessage;
  const safeTitle = lang !== 'ko' && containsHangul(rawTitle) ? '' : rawTitle;
  const title = safeTitle || translateNow('maintenance.defaultTitle', null, 'Server maintenance');
  const remainingText = formatRemaining(maintenance.remaining_ms ?? maintenance.remainingMs);
  const blockMinutes = toNumber(maintenance.block_before_minutes ?? maintenance.blockBeforeMinutes, 5);

  if (message) {
    const alreadyHasCountdown = /(^|\s)(\d+\s*(?:일|시간|분|초)\s*뒤|남은\s*시간|remaining)/i.test(message);
    return remainingText && !alreadyHasCountdown
      ? translateNow('maintenance.messageWithCountdown', { time: remainingText, message }, `${message} in ${remainingText}`)
      : message;
  }

  if (remainingText) {
    return translateNow('maintenance.scheduledWithCountdown', { time: remainingText, title, minutes: blockMinutes }, `${title} is scheduled in ${remainingText}. Games will be stopped ${blockMinutes} minutes before the update for a safe shutdown.`);
  }
  return translateNow('maintenance.scheduled', { title }, `${title} is scheduled. Please follow the notice for a safe shutdown.`);
}
